import axios, { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { Accensione, Dispositivi, Risorse, RisorseTipologie } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import { memoParseISO } from "../utils/dateUtils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCallback, useState } from "react";
import { queryClient } from "../App";
import { useSnackbar } from "notistack";

export function AccensioniProgTable(props: { onEdit: (acc: Accensione) => void }) {
  const { onEdit } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);

  const openDelete = (id: number) => {
    setDeleteId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const { isLoading, data } = useQuery<unknown, AxiosError, { accensioni: Accensione[] }>(
    ["accensioni-prog"],
    () => axios.get(API_URL + "accensioni-prog").then((res) => res.data)
  );

  const { isLoading: isLoading2, data: data2 } = useQuery<
    unknown,
    AxiosError,
    { dispositivi: Dispositivi[] }
  >(["dispositivi/risorseClub"], () =>
    axios.get(API_URL + "dispositivi/risorseClub/").then((res) => res.data)
  );

  const { isLoading: isLoading3, data: data3 } = useQuery<
    unknown,
    AxiosError,
    { risorse: (Risorse & { tipologia: RisorseTipologie })[] }
  >(["risorse/findAll"], () => axios.get(API_URL + "risorse/findAll/").then((res) => res.data));

  const { mutate } = useMutation(
    (id: number) => {
      return axios.delete(API_URL + "accensioni-prog/" + id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Accensione eliminato", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const eliminaClick = useCallback(() => {
    mutate(deleteId);
    setOpen(false);
  }, [mutate, setOpen, deleteId]);

  if (isLoading || isLoading2 || isLoading3) return <Loading />;

  const res = data?.accensioni.map((acc) => {
    const dispositivo = data2?.dispositivi.find((d) => d.id === acc.idDispositivo);
    const risorsa = data3?.risorse.find((r) => r.id === dispositivo?.idRisorse);

    return { ...acc, dispositivo, risorsa };
  });

  return (
    <>
      <TableContainer>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Risorse</TableCell>
              <TableCell>Dispositivo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Ora Accensione</TableCell>
              <TableCell>Ora Spegnimento</TableCell>
              <TableCell>Dal</TableCell>
              <TableCell>Al</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {res?.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell>
                  <IconButton sx={{ p: 0 }} onClick={() => onEdit(acc)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell>{acc.risorsa?.denominazione}</TableCell>
                <TableCell>{acc.dispositivo?.descrizione}</TableCell>
                <TableCell>{acc.spegnimento ? "Spegni" : "Accendi"}</TableCell>
                <TableCell>
                  {acc.oraAccensione &&
                    format(memoParseISO(acc.oraAccensione.slice(0, -1)), "HH:mm")}
                </TableCell>
                <TableCell>
                  {acc.oraSpegnimento &&
                    format(memoParseISO(acc.oraSpegnimento.slice(0, -1)), "HH:mm")}
                </TableCell>
                <TableCell>
                  {acc.dataDal && format(memoParseISO(acc.dataDal.slice(0, -1)), "dd/MM/yyyy")}
                </TableCell>
                <TableCell>
                  {acc.dataFine && format(memoParseISO(acc.dataFine.slice(0, -1)), "dd/MM/yyyy")}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <IconButton aria-label="elimina" sx={{ p: 0 }} onClick={() => openDelete(acc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open}>
        <DialogContent>Sei sicuro di voler eliminare?</DialogContent>
        <DialogActions>
          <Button onClick={eliminaClick} autoFocus variant="text" color="error">
            Elimina
          </Button>
          <Button onClick={handleClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
